import { useState } from 'react';
import { useLang } from 'hooks/lang';

import { actions, FullTask, loaders, taskStatusByCode, isOwner } from 'entities';

import { Skeleton, Input, Button, ResponsiveOverlay, Alert, Column } from 'components/Blocks';
import { SendIcon } from 'components/Icons';

import { SampleIdsDisplay } from './Ids';

import { useUser } from 'hooks';

import { wrapFormAction } from 'utils/html';

import { TaskOptionOverlayProps } from '../createTaskOptions';

export function SampleIdsModal({ close, isOpen, task, updateTask }: TaskOptionOverlayProps) {
  const loggedUser = useUser();

  const [owId, setOwId] = useState('');

  const { t } = useLang();

  const { errorOrLoading } = loaders.projects.taskLegacySampleIds.use({
    blockLoad: !task?.requireSampleProjectIds,

    params: { projectId: task.projectId, taskId: task.id },

    onDataChange: (sampleIds) => updateTask({ sampleIds }),
  });

  const sampleIds = (task as FullTask)?.sampleIds || [];

  const btnBlocked = owId.length <= 2 || taskStatusByCode[task.status].blocks.updateIds;

  const saveId = actions.projects.addLegacySampleIdToTask.use({
    block: btnBlocked,

    params: {
      owId,
      project: task.projectId,
      task: task.id,
    },

    onSuccess: (sampleIds) => {
      setOwId('');

      updateTask({ sampleIds, canFinish: true });
    },
  });

  const isTaskOwner = isOwner(task, loggedUser);

  return (
    <ResponsiveOverlay
      default="sheet"
      isOpen={isOpen}
      close={close}
      title={t('sample-ids')}
      allowEscape
    >
      <Alert level="warning" title={t('legacy-warning')}>
        {t('legacy-warning-desc')}
      </Alert>

      {errorOrLoading ? (
        <>
          <Skeleton width="40%" height={32} />

          <Skeleton width="100%" height={48} />

          <Skeleton width="25%" height={42} />
        </>
      ) : (
        <>
          <SampleIdsDisplay ids={sampleIds} />

          {isTaskOwner ? (
            <Column>
              <span>{t('please-add-all-related-offerwise-ids')}</span>

              <form className="relative" onSubmit={wrapFormAction(saveId.onClick)}>
                <Input
                  value={owId}
                  onChange={({ target }) => setOwId(target.value.trim())}
                  placeholder={t('type-new-offerwise-id')!}
                />

                <Button
                  type="submit"
                  disabled={btnBlocked}
                  className="absolute bottom-1 right-1 top-1 h-auto w-14"
                  loading={saveId.loading}
                >
                  <SendIcon />
                </Button>
              </form>
            </Column>
          ) : (
            <p className="text-center text-xs italic">{t('only-task-owner-can-modify-this')}</p>
          )}
        </>
      )}
    </ResponsiveOverlay>
  );
}
